import "core-js/stable";
import "regenerator-runtime/runtime";

import React from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  hasRole,
  useActiveRoles,
  hasAtLeastOneRole,
} from "./utils/permissionsUtils";
import { routesConfig } from "./routesConfig";
import { RoleEnum } from "./types/RoleEnum";
import Settings from "./settings";
import { AccessDenied } from "./components/AccessDenied";
import { EXTERNAL_ROUTES } from "./types/routePaths";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

const BrLegalDeposits = (props) => {
  const activeRoles = useActiveRoles();
  const {
    showSettings,
    match: { path },
    stripes,
  } = props;

  if (showSettings) {
    return <Settings {...props} />;
  }

  // Redirect users who haven't selected an organization yet
  // When no organization is selected, activeRoles is an empty array
  const needsOrgSelection =
    Array.isArray(activeRoles) && activeRoles.length === 0;

  if (needsOrgSelection) {
    return <Redirect to={EXTERNAL_ROUTES.USERS_MODULE_SELECT_ORG} />;
  }

  const connectedRoutes = Object.keys(routesConfig).reduce((acc, key) => {
    acc[key] = stripes.connect(routesConfig[key].component);
    return acc;
  }, {});

  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        {Object.keys(routesConfig).map((key) => {
          const { path: routePath, roles } = routesConfig[key];
          const Component = connectedRoutes[key];

          const isSuperUser = hasRole(activeRoles, RoleEnum.SUPERUSER);
          const hasRequiredRole = roles
            ? hasAtLeastOneRole(activeRoles, roles)
            : true;
          const hasAccess = isSuperUser || hasRequiredRole;

          return (
            <Route
              key={key}
              path={`${path}${routePath}`}
              render={(routeProps) =>
                !hasAccess ? (
                  <AccessDenied showNavigation />
                ) : (
                  <Component {...props} {...routeProps} />
                )
              }
            />
          );
        })}
        <Route
          render={() => {
            const isSuperUser = hasRole(activeRoles, RoleEnum.SUPERUSER);

            if (isSuperUser) {
              const firstRoute = Object.values(routesConfig)[0];
              return <Redirect to={`${path}${firstRoute.path}`} />;
            }

            const accessibleRoute = Object.values(routesConfig).find(
              (route) => {
                if (!route.roles) return true;
                return hasAtLeastOneRole(activeRoles, route.roles);
              },
            );

            if (accessibleRoute) {
              return <Redirect to={`${path}${accessibleRoute.path}`} />;
            }

            return <AccessDenied showNavigation />;
          }}
        />
      </Switch>
    </QueryClientProvider>
  );
};

BrLegalDeposits.propTypes = {
  match: PropTypes.object.isRequired,
  showSettings: PropTypes.bool,
  stripes: PropTypes.shape({
    connect: PropTypes.func,
  }),
};

export default BrLegalDeposits;
